import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import posthog from 'posthog-js'

import DotsLoading from 'components/loadings/dotsLoading'
import supportMascot from 'assets/images/landing/support-mascot.png'
import supportMascotLight from 'assets/images/landing/support-mascot-light.png'
import { HAMYAN_SITE_URL } from 'modules/landing/links'
import { ISupportBotTurn, askSupportBotAsGuest } from 'services/supportBot'

/**
 * «پشتیبان هوشمند» on the قرعه‌کشی landing, for a visitor with no account.
 *
 * The same bot the app ships, reached through its own endpoint: on the server
 * the member path derives every permission from the caller's JWT, and this
 * caller has none, so it answers from the knowledge base and can read nothing.
 * See `support_bot/guest.py`.
 *
 * Why it is here at all: the page asks somebody to hand over their fund, and
 * the questions that stops a person — «چقدر می‌شود؟», «اطلاعاتم کجا می‌رود؟»,
 * «اعضایم چه می‌بینند؟» — are exactly the ones a landing page cannot answer in
 * advance without becoming the wall of text this page was rewritten to stop
 * being. So they get asked instead.
 *
 * The launcher sits top-left at every size — the corner nothing else uses in
 * this RTL layout. It used to move to the bottom on wide screens, which is the
 * web convention and was still wrong here: somebody comparing the page on a
 * laptop and a phone should find the same thing in the same place, and this
 * page is read on both.
 *
 * The mark is the app's OWN support icon (`assets/images/home/Headphone.svg`),
 * not something drawn for this page. It is already the headset a Hamyan user
 * sees next to «پشتیبانی» on the home screen, in the exact brand green — so a
 * manager who installs the app afterwards meets the same picture, and we are
 * not maintaining a second support icon that will drift from the first.
 *
 * The support button breathes — a slow opacity pulse, not a bounce. The ask
 * was for something that draws the eye into clicking it; a movement that
 * competes with the hero for attention would win and cost the page its first
 * sentence, so it dims and returns instead of jumping.
 */

const SUGGESTIONS = [
    'چقدر هزینه دارد؟',
    'انتقال صندوق چطور انجام می‌شود؟',
    'اعضای من چه چیزی می‌بینند؟',
]

/** Sent to the server as context, capped so an old thread cannot grow forever. */
const MAX_TURNS = 8

interface Props {
    /** the invitation code, so a question is attributable like every other event */
    code: string | null
    /** where on the page this launcher was rendered, for the funnel */
    surface: 'intro' | 'demo'
}

export default function LandingSupportChat({ code, surface }: Props) {
    const [open, setOpen] = useState(false)
    const [turns, setTurns] = useState<ISupportBotTurn[]>([])
    const [draft, setDraft] = useState('')
    const [pending, setPending] = useState(false)
    const [failed, setFailed] = useState(false)
    const scroller = useRef<HTMLDivElement | null>(null)

    const track = (event: string, props: Record<string, unknown> = {}) => {
        posthog.capture(event, { landing_code: code, surface, ...props })
    }

    useEffect(() => {
        // Keep the newest turn in view without yanking the page behind the
        // panel, which is why this scrolls the panel and not the window.
        const node = scroller.current
        if (node) node.scrollTop = node.scrollHeight
    }, [turns, pending])

    const ask = async (question: string) => {
        const text = question.trim()
        if (!text || pending) return

        setDraft('')
        setFailed(false)
        setPending(true)
        const asked: ISupportBotTurn = { role: 'user', content: text }
        setTurns((current) => [...current, asked])
        track('lottery_support_asked', { length: text.length })

        try {
            const data = await askSupportBotAsGuest({
                question: text,
                history: turns.slice(-MAX_TURNS),
            })
            setTurns((current) => [
                ...current,
                { role: 'assistant', content: data.answer },
            ])
            // `outcome` is the point: four different server failures answer 200
            // with the same canned Persian sentence, so a resolved promise is
            // not evidence anybody was helped.
            track('lottery_support_answered', {
                outcome: data.outcome,
                topic: data.topic,
                provider_ms: data.provider_ms,
            })
        } catch {
            setFailed(true)
            track('lottery_support_failed', {})
        } finally {
            setPending(false)
        }
    }

    // Two buttons, always within reach: ask, and find out what Hamyan is.
    //
    // WHERE THEY SIT. Bottom-right on a phone, top-left on anything wider.
    // Both corners were tried top-left first, at the owner's request; seeing it
    // on an actual handset the verdict was «خیلی بده» — on a 390px screen that
    // pair sits directly over where the page starts, and the reader's eye lands
    // on two floating cards before it lands on the headline. Bottom-right is
    // the thumb's corner and the one this RTL layout leaves empty. On a laptop
    // the top-left corner is far from the text and stays as it was.
    //
    // Both launchers carry a label, always. An unlabelled round icon in a
    // corner is a guess the reader has to make while they are trying to read
    // something else — and the two sitting together made it worse, because a
    // guess about one is a guess about both.
    //
    // On a phone: the mark with a caption under it, small enough to stay out of
    // the way. On anything wider: the mark beside a sentence, because there is
    // room for the sentence and a laptop reader is further from the screen.
    //
    // The demo screen pins a call-to-action bar across the bottom (113px on a
    // 390×844 handset), so on that surface the pair is lifted clear of it. A
    // launcher that covers «صندوقم را منتقل کنید» would cost the page the one
    // press it exists to get.
    const launcher = (
        <div
            className={`fixed right-3 z-[930] flex flex-col gap-2.5 sm:bottom-auto sm:left-5 sm:right-auto sm:top-5 sm:gap-3 ${
                surface === 'demo' ? 'bottom-[124px]' : 'bottom-4'
            }`}
        >
            <button
                type="button"
                aria-label="پشتیبانی آنلاین همیان"
                onClick={() => {
                    setOpen(true)
                    track('lottery_support_opened', {})
                }}
                className="flex w-[74px] flex-col items-center gap-1 rounded-2xl border border-[#CDE3BF] bg-white/95 px-1.5 py-2 shadow-md backdrop-blur active:scale-95 sm:w-auto sm:flex-row sm:gap-2.5 sm:px-3 sm:py-2.5"
            >
                <img
                    src={supportMascot}
                    alt=""
                    aria-hidden
                    className="h-9 w-9 shrink-0 animate-breathe rounded-full sm:h-11 sm:w-11"
                />
                <span className="text-[10px] font-bold leading-tight text-brand-deep sm:hidden">
                    سؤالتو بپرس
                </span>
                <span className="hidden text-right leading-tight sm:block">
                    <span className="block text-sm font-bold text-black">
                        پشتیبانی آنلاین
                    </span>
                    <span className="mt-0.5 block text-[11px] text-text-secondary">
                        سؤالت را همین‌جا بپرس
                    </span>
                </span>
            </button>

            <button
                type="button"
                aria-label="دربارهٔ همیان"
                onClick={() => {
                    track('lottery_about_opened', {})
                    window.open(
                        HAMYAN_SITE_URL,
                        '_blank',
                        'noopener,noreferrer'
                    )
                }}
                className="flex w-[74px] flex-col items-center gap-1 rounded-2xl border border-divider bg-white/95 px-1.5 py-2 shadow-md backdrop-blur active:scale-95 sm:w-auto sm:flex-row sm:gap-2.5 sm:px-3 sm:py-2.5"
            >
                <span
                    aria-hidden
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-lg font-bold leading-none text-brand-deep sm:h-11 sm:w-11"
                >
                    ؟
                </span>
                <span className="text-[10px] font-bold leading-tight text-text-body sm:hidden">
                    همیان چیه؟
                </span>
                <span className="hidden text-right leading-tight sm:block">
                    <span className="block text-sm font-bold text-black">
                        دربارهٔ همیان
                    </span>
                    <span className="mt-0.5 block text-[11px] text-text-secondary">
                        سایت را باز کن
                    </span>
                </span>
            </button>
        </div>
    )

    if (!open) return createPortal(launcher, document.body)

    return createPortal(
        <div className="fixed inset-0 z-[960] flex sm:items-center sm:justify-center">
            <div
                className="absolute inset-0 bg-black/50"
                onClick={() => {
                    setOpen(false)
                    track('lottery_support_closed', { turns: turns.length })
                }}
            />
            <div
                // Full page on a phone. A bottom sheet was leaving a strip of
                // the landing showing above it, so the reader was reading an
                // answer through a hole in the page they were asking about —
                // and the keyboard then ate most of what was left. `dvh`, not
                // `vh`, because the mobile browser bar changes its mind.
                className="relative flex h-[100dvh] w-full flex-col bg-white sm:h-[620px] sm:max-h-[85dvh] sm:w-[420px] sm:rounded-[16px] sm:shadow-xl"
                role="dialog"
                aria-label="پشتیبان هوشمند همیان"
            >
                <div className="mx-auto flex w-full max-w-[560px] items-center gap-2 border-b border-divider px-4 py-3">
                    <img
                        src={supportMascotLight}
                        alt=""
                        aria-hidden
                        className="h-9 w-9 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-black">
                            پشتیبان هوشمند همیان
                        </p>
                        <p className="text-[11px] text-text-secondary">
                            هرچه دربارهٔ همیان می‌خواهید بپرسید
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="بستن"
                        onClick={() => {
                            setOpen(false)
                            track('lottery_support_closed', {
                                turns: turns.length,
                            })
                        }}
                        className="h-8 w-8 shrink-0 text-text-secondary"
                    >
                        ✕
                    </button>
                </div>

                <div
                    ref={scroller}
                    className="flex-1 overflow-y-auto px-4 py-3"
                >
                    {turns.length === 0 && (
                        <>
                            <p className="text-xs leading-6 text-text-secondary">
                                این گفت‌وگو به حساب شما وصل نیست و چیزی از
                                صندوقتان نمی‌بیند — دربارهٔ خودِ همیان بپرسید.
                            </p>
                            <div className="mt-3 space-y-2">
                                {SUGGESTIONS.map((item) => (
                                    <button
                                        key={item}
                                        type="button"
                                        onClick={() => {
                                            track(
                                                'lottery_support_suggestion',
                                                {
                                                    question: item,
                                                }
                                            )
                                            void ask(item)
                                        }}
                                        className="block w-full rounded-[10px] border border-divider px-3 py-2 text-right text-xs text-text-body active:bg-[#FAFAFA]"
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    {turns.map((turn, index) => (
                        <div
                            key={`${turn.role}-${index}`}
                            className={`mb-2 flex ${
                                turn.role === 'user'
                                    ? 'justify-start'
                                    : 'justify-end'
                            }`}
                        >
                            <p
                                className={`max-w-[85%] whitespace-pre-wrap rounded-[12px] px-3 py-2 text-xs leading-6 ${
                                    turn.role === 'user'
                                        ? 'bg-brand-soft text-brand-deep'
                                        : 'bg-[#F6F7F9] text-text-body'
                                }`}
                            >
                                {turn.content}
                            </p>
                        </div>
                    ))}

                    {pending && (
                        <div className="flex justify-end">
                            <DotsLoading />
                        </div>
                    )}
                    {failed && (
                        <p className="mt-2 text-xs leading-6 text-[#E0564C]">
                            پاسخ نیامد. دوباره بفرستید، یا اگر عجله دارید دکمهٔ
                            «صندوقم را به همیان منتقل کنید» را بزنید تا همکاران
                            تماس بگیرند.
                        </p>
                    )}
                </div>

                <form
                    className="mx-auto flex w-full max-w-[560px] items-center gap-2 border-t border-divider p-3"
                    onSubmit={(event) => {
                        event.preventDefault()
                        void ask(draft)
                    }}
                >
                    <input
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        placeholder="سؤالتان را بنویسید…"
                        className="h-10 flex-1 rounded-[10px] border border-divider px-3 text-xs outline-none focus:border-brand"
                    />
                    <button
                        type="submit"
                        disabled={!draft.trim() || pending}
                        className="h-10 shrink-0 rounded-[10px] bg-brand px-4 text-xs font-bold text-white disabled:opacity-40"
                    >
                        بپرس
                    </button>
                </form>
            </div>
        </div>,
        document.body
    )
}
